import React from "react";
import PropTypes from "prop-types";

const valueType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.bool,
]);

const propTypes = {
  labels: PropTypes.shape({
    left:   PropTypes.shape({ title: PropTypes.string.isRequired, value: valueType }),
    center: PropTypes.shape({ title: PropTypes.string.isRequired, value: valueType }),
    right:  PropTypes.shape({ title: PropTypes.string.isRequired, value: valueType }),
  }),
  onChange: PropTypes.func.isRequired,
  styles: PropTypes.object,
};

const defaultProps = {
  labels: {
    left:   { title: "left",   value: "left" },
    center: { title: "center", value: "center" },
    right:  { title: "right",  value: "right" },
  },
  onChange: (value) => console.info("value:", value),
};

const POSITIONS = ["left", "center", "right"];
const OFFSET_MAP = { left: "0%", center: "100%", right: "200%" };

class TripleToggleSwitch extends React.Component {
  constructor(props) {
    super(props);
    // Track active position in a plain instance variable — NOT in state
    // This avoids React re-renders entirely for the visual update
    this._position = "left";
    this.state = { switchPosition: "left" };
    this.sliderRef = React.createRef();
    this.buttonRefs = {
      left:   React.createRef(),
      center: React.createRef(),
      right:  React.createRef(),
    };
  }

  // Called once after mount to set initial slider position without transition
  componentDidMount() {
    this._applyPosition("left", false);
  }

  _applyPosition(value, animate = true) {
    const slider = this.sliderRef.current;
    if (!slider) return;

    // Disable transition temporarily for instant jumps (e.g. on mount)
    if (!animate) {
      slider.style.transition = "none";
    }

    slider.style.transform = `translateX(${OFFSET_MAP[value]})`;

    // Re-enable transition after paint
    if (!animate) {
      requestAnimationFrame(() => {
        if (this.sliderRef.current) {
          this.sliderRef.current.style.transition = "";
        }
      });
    }

    // Update button colors immediately
    POSITIONS.forEach((pos) => {
      const btn = this.buttonRefs[pos]?.current;
      if (!btn) return;
      btn.style.color = pos === value ? "#fff" : "#64748b";
      btn.style.fontWeight = pos === value ? "700" : "600";
    });
  }

  handleChange = (value) => {
    if (this.props.loadingTransaction) return;
    if (this._position === value) return;

    this._position = value;

    // Visual update happens SYNCHRONOUSLY — zero delay
    this._applyPosition(value, true);

    // Parent callback and React state update happen after — non-blocking
    this.props.onChange(value);
    // Only update state so parent-driven re-renders stay in sync
    // We deliberately do NOT call setState here to avoid React overwriting
    // the transform we just set via the DOM ref
  };

  render() {
    const { labels, loadingTransaction } = this.props;

    return (
      <>
        <div className={"tts-root" + (loadingTransaction ? " tts-root--disabled" : "")}>
          {/* NO inline style on slider — position is 100% controlled by ref */}
          <div ref={this.sliderRef} className="tts-slider" />

          {POSITIONS.map((pos) => (
            <button
              key={pos}
              ref={this.buttonRefs[pos]}
              type="button"
              disabled={!!loadingTransaction}
              onClick={() => this.handleChange(pos)}
              className="tts-option"
            >
              {labels[pos]?.title ?? pos}
            </button>
          ))}
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

          .tts-root {
            font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
            position: relative;
            display: inline-flex;
            align-items: center;
            width: 240px;
            height: 36px;
            background: #f1f5f9;
            border-radius: 10px;
            border: 1.5px solid #e2e8f0;
            padding: 3px;
            box-sizing: border-box;
            overflow: hidden;
          }
          .tts-root--disabled {
            opacity: 0.55;
            pointer-events: none;
            cursor: not-allowed;
          }
          .tts-slider {
            position: absolute;
            top: 3px;
            left: 3px;
            width: calc((100% - 6px) / 3);
            height: calc(100% - 6px);
            background: linear-gradient(135deg, #1d4ed8, #2563eb);
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(37,99,235,0.28);
            transition: transform 0.2s cubic-bezier(0.16,1,0.3,1);
            z-index: 1;
            will-change: transform;
            /* Start at left by default; componentDidMount sets it properly */
            transform: translateX(0%);
          }
          .tts-option {
            position: relative;
            z-index: 2;
            flex: 1;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
            font-size: 12.5px;
            font-weight: 600;
            color: #64748b;
            background: none;
            border: none;
            cursor: pointer;
            border-radius: 7px;
            user-select: none;
            white-space: nowrap;
            padding: 0 4px;
            /* No CSS transition on color — JS sets it instantly */
          }
          .tts-option:first-child {
            /* Left button starts as active */
            color: #fff;
            font-weight: 700;
          }
          .tts-option:disabled {
            cursor: not-allowed;
          }
        `}</style>
      </>
    );
  }
}

TripleToggleSwitch.propTypes = propTypes;
TripleToggleSwitch.defaultProps = defaultProps;

export default TripleToggleSwitch;